import jwtDecode from 'jwt-decode';
import { Cookies } from 'react-cookie';

import _get from 'lodash/get';
import _lowerCase from 'lodash/toLower';
import _isString from 'lodash/isString';

import { enrichmentExpressions } from 'constants/enrichmentFunctions';

const debounce = (fn, wait = 300) => {
    let debounced = false;
    let resetDebouncedTimeout = null;
    return (...args) => {
        if (!debounced) {
            debounced = true;
            fn(...args);
            resetDebouncedTimeout = setTimeout(() => {
                debounced = false;
            }, wait);
        } else {
            clearTimeout(resetDebouncedTimeout);
            resetDebouncedTimeout = setTimeout(() => {
                debounced = false;
                fn(...args);
            }, wait);
        }
    };
};

const fileDownload = (data, fileName, responseType = 'application/excel') => {
    const url = URL.createObjectURL(new Blob([data], { type: responseType }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};


/**
 * Checks if user is authenticated
 */
const isUserAuthenticated = () => {
    const user = getLoggedInUser();
    if (!user) {
        return false;
    }
    const decoded = jwtDecode(user.token);
    const currentTime = Date.now() / 1000;

    return true;
};

/**
 * Returns the logged in user
 */
const getLoggedInUser = () => {
    const cookies = new Cookies();
    const user = cookies.get('user');
    return user ? (typeof user == 'object' ? user : JSON.parse(user)) : null;
};


const getExecExpression = (payload, isLowerCase = false) => {
    let expression = 'NOT_IMPLEMENTED';
    switch (_get(payload, 'formType', '')) {
        case 'NUMERIC_FUNCTIONS': {
            const operationType = _get(payload.formProps, 'operationType.value', '');
            const fieldOne = _get(payload.formProps, 'fieldOne', null);
            const fieldTwo = _get(payload.formProps, 'fieldTwo', null);
            if (fieldOne !== null) {
                if (isLowerCase) {
                    fieldOne.label = _lowerCase(fieldOne.label);
                }
                if (operationType === 'ABS') {
                    expression = `ABS(${fieldOne.label})`;
                } else if (operationType === 'ROUND') {
                    expression = `ROUND(${fieldOne.label})`;
                } else if (operationType === 'ROUNDUP') {
                    expression = `CEIL(${fieldOne.label})`;
                } else if (operationType === 'ROUNDDOWN') {
                    expression = `FLOOR(${fieldOne.label})`;
                }
            }
            if (fieldOne !== null && fieldTwo !== null) {
                if (isLowerCase) {
                    fieldOne.label = _lowerCase(fieldOne.label);
                    fieldTwo.label = _lowerCase(fieldTwo.label);
                }
                if (operationType === 'MAX') {
                    expression = `GREATEST(${fieldOne.label}, ${fieldTwo.label})`;
                } else if (operationType === 'MIN') {
                    expression = `LEAST(${fieldOne.label}, ${fieldTwo.label})`;
                } else if (operationType === 'SUM') {
                    expression = `(${fieldOne.label} + ${fieldTwo.label})`;
                } else if (operationType === 'SUBTRACT') {
                    expression = `(${fieldOne.label} - ${fieldTwo.label})`;
                } else if (operationType === 'DIVIDE') {
                    expression = `(${fieldOne.label} / ${fieldTwo.label})`;
                } else if (operationType === 'MULTIPLY') {
                    expression = `(${fieldOne.label} * ${fieldTwo.label})`;
                } else if (operationType === 'FIXED_VALUE') {
                    expression = `"(${fieldOne.label} * ${fieldTwo.label})"`;
                }
            }
            break;
        }
        case 'STRING_FUNCTIONS': {
            const formProps = _get(payload, 'formProps', {});
            const operationType = _get(formProps, 'stringOperation.value', '').toLocaleUpperCase();
            const fieldOne = _get(payload.formProps, 'field.value', null);
            const fieldTwo = _get(payload.formProps, 'fieldTwo', null);
            if (operationType === 'TRIM') {
                const trimPosition = _get(formProps, 'trimPosition.value', '');
                if (trimPosition === 'fromStart')
                    expression = `TRIM(LEADING FROM ${fieldOne}) `;
                if (trimPosition === 'fromEnd')
                    expression = `TRIM(TRAILING FROM ${fieldOne}) `;
                if (trimPosition === '' || trimPosition === 'default')
                    expression = `TRIM(${fieldOne})`;
            } else if (operationType === 'TOUPPERCASE') {
                expression = `UPPER(${fieldOne})`;
            } else if (operationType === 'TOLOWERCASE') {
                expression = `LOWER(${fieldOne})`;
            } else if (operationType === 'EXTRACT') {
                const numberOfCharacters = _get(payload, 'numberOfCharacters', '0');
                const startingPosition = _get(payload, 'startingPosition', '0');
                const startFrom = _get(payload, 'startFrom', '0');
                expression = `SUBSTR(${fieldOne}, ${startingPosition}, ${numberOfCharacters})`;
            } else if (operationType === 'FIXED_VALUE') {
                const fixedValue = _get(payload, 'fixedValue', '0');
                expression = `'${fixedValue}' as ${fieldOne}`;
            }
            break;
        }

    }
    return expression;
};

const getExpressionNewCalculationFields = (operationType) => {
    if (enrichmentExpressions[operationType]) {
        return enrichmentExpressions[operationType];
    }

    let expression = 'NOT_IMPLEMENTED';
    // const numerics = ['ABS', 'ROUND', 'ROUNDUP', 'ROUNDDOWN', 'MAX', 'GREATEST', 'SUM', 'SUBTRACT', 'DIVIDE', 'MULTIPLY', 'FIXED_VALUE'];

    if (operationType === 'ABS') {
        expression = `ABS($placeholder)`;
    } else if (operationType === 'ROUND') {
        expression = `ROUND($placeholder)`;
    } else if (operationType === 'ROUNDUP') {
        expression = `CEIL($placeholder)`;
    } else if (operationType === 'ROUNDDOWN') {
        expression = `FLOOR($placeholder)`;
    } else if (operationType === 'MAX') {
        expression = `GREATEST($placeholder, $placeholder)`;
    } else if (operationType === 'MIN') {
        expression = `LEAST($placeholder, $placeholder)`;
    } else if (operationType === 'SUM') {
        expression = `($placeholder + $placeholder)`;
    } else if (operationType === 'SUBTRACT') {
        expression = `($placeholder - $placeholder)`;
    } else if (operationType === 'DIVIDE') {
        expression = `($placeholder / $placeholder)`;
    } else if (operationType === 'MULTIPLY') {
        expression = `($placeholder * $placeholder)`;
    } else if (operationType === 'FIXED_VALUE') {
        expression = `'<Value>'`;
    } else if (operationType === 'FROMSTART')
        expression = `TRIM(LEADING FROM $placeholder) `;
    else if (operationType === 'FROMEND')
        expression = `TRIM(TRAILING FROM $placeholder) `;
    else if (operationType === '' || operationType === 'DEFAULT' || operationType === 'TRIM') {
        expression = `TRIM($placeholder)`;
    } else if (operationType === 'TOUPPERCASE') {
        expression = `UPPER($placeholder)`;
    } else if (operationType === 'TOLOWERCASE') {
        expression = `LOWER($placeholder)`;
    } else if (operationType === 'EXTRACT') {
        expression = `EXTRACT(<FIELD> FROM $placeholder)`;
    } else if (operationType === 'DAY_DIFFERENCE') {
        expression = `$placeholder - $placeholder`;
    } else if (operationType === 'ADD_DAYS') {
        expression = ` $placeholder + <NO_OF_DAYS> * INTERVAL '1 day'`;
    } else if (operationType === 'TODAY') {
        expression = ` current_date `;
    } else if (operationType === 'STRINGTODATE') {
        expression = ` TO_DATE($placeholder, '<DATE_FORMAT>') `;
    } else if (operationType === 'STRINGTODECIMAL') {
        expression = ` CAST ( $placeholder AS DECIMAL)`;
    } else if (operationType === 'DATETOSTRING') {
        expression = `CAST( $placeholder AS VARCHAR)`;
    } else if (operationType === 'DECIMALTOSTRING') {
        expression = ` CAST( $placeholder AS VARCHAR)`;
    } else if (operationType === 'IF') {
        expression = ` CASE WHEN  $placeholder THEN $placeholder ELSE $placeholder  END `;
    } else if (operationType === 'ISNULL') {
        expression = `$placeholder IS NULL`;
    } else if (operationType === 'MATCHES') {
        expression = `  $placeholder LIKE '<PATTERN>' `;
    } else if (operationType === 'FIND') {
        expression = ` POSITION('<PATTERN>' in $placeholder) `;
    } else if (operationType === 'MID') {
        expression = ` substring( $placeholder from <POSITION> for <SIZE> ) `;
    } else if (operationType === 'LEFT') {
        expression = ` LEFT( $placeholder, <SIZE> ) `;
    } else if (operationType === 'RIGHT') {
        expression = ` RIGHT( $placeholder, <SIZE> )  `;
    } else if (operationType === 'LEN') {
        expression = ` LENGTH($placeholder) `;
    } else if (operationType === 'REPLACE') {
        expression = ` REPLACE( $placeholder, '<OLD_TEXT>', '<NEW_TEXT>' )`;
    }

    return expression;
};

const parseJSON = (jsonStr = {}) => {
    if (_isString(jsonStr)) {
        let result = null;
        try {
            result = JSON.parse(jsonStr);
        } catch (e) {
            console.log(e);
        }
        return result;
    }
    return {};

};

export {
    isUserAuthenticated,
    getLoggedInUser,
    debounce,
    fileDownload,
    getExecExpression,
    getExpressionNewCalculationFields,
    parseJSON,
};
  